import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useLocation, useNavigate } from "react-router-dom"

import { cn } from "@/domains/library"
import { navItems } from "@/domains/navigation/nav-items"
import useResponsive from "@/hooks/use-responsive"

export const NavigationDropdownBar = observer(({ className }: { className?: string }) => {
  const { isTouchPrimary } = useResponsive()
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [openItem, setOpenItem] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const handleInternalNavItemClick = (href: string) => {
    setOpenItem(null)
    navigate(href)
  }

  const handleParentItemClick = (item: (typeof navItems)[number]) => {
    if (item.subItems.length > 0) {
      if (isTouchPrimary) {
        setOpenItem((current) => (current === item.id ? null : item.id))
        return
      }

      if (item.id === "more") {
        setOpenItem((current) => (current === item.id ? null : item.id))
        return
      }
    }

    handleInternalNavItemClick(item.href)
  }

  const isItemOpen = (itemId: string) => {
    if (isTouchPrimary) {
      return openItem === itemId
    }

    return hoveredItem === itemId || openItem === itemId
  }

  return (
    <nav className={cn("flex items-center space-x-2 text-sm text-t100", className)}>
      {navItems.map((item) => (
        <div
          key={item.id}
          className="relative cursor-pointer"
          onMouseEnter={isTouchPrimary ? undefined : () => setHoveredItem(item.id)}
          onMouseLeave={isTouchPrimary ? undefined : () => setHoveredItem(null)}
        >
          <button
            onClick={() => handleParentItemClick(item)}
            className={cn(
              "x-6 group flex cursor-pointer items-center gap-2 px-4 py-2 transition-colors",
              "rounded-lg",
              item.id !== "swap" ? "rounded-b-none" : "",
              isItemOpen(item.id) ? "bg-p400" : "",
            )}
          >
            <span className="font-medium">{item.label}</span>
          </button>

          {isItemOpen(item.id) && item.subItems.length > 0 ? (
            <div
              className={cn(
                "card card-sm",
                "absolute left-0 top-full z-50 mt-0 w-48 overflow-hidden",
                "rounded-lg rounded-t-sm rounded-tl-none",
                "bg-p400 text-t200",
              )}
            >
              {item.subItems.map((subItem) => {
                if (subItem.isExternal) {
                  return (
                    <div
                      key={subItem.id}
                      className={cn(
                        "w-full text-left transition-colors ",
                        subItem.isActive(location) ? "bg-p400" : "bg-p300 hover:bg-p400",
                      )}
                    >
                      <a
                        href={subItem.href}
                        target="_blank"
                        rel="noreferrer noopen"
                        className="flex items-center gap-3 px-4 py-3"
                      >
                        <span className={subItem.iconName} />
                        <span>{subItem.label}</span>
                      </a>
                    </div>
                  )
                }
                return (
                  <button
                    key={subItem.id}
                    onClick={() => handleInternalNavItemClick(subItem.href)}
                    className={cn(
                      "w-full cursor-pointer px-4 py-3 text-left transition-colors",
                      subItem.isActive(location)
                        ? "bg-p400 text-t100"
                        : "text-t100 hover:bg-p400-light hover:text-t100-light",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span className={subItem.iconName} />
                      <span>{subItem.label}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          ) : null}
        </div>
      ))}
    </nav>
  )
})
